import { useMemo } from "react";
import { ShieldAlert, User, Zap, Clock, Sliders, Archive, Layers, Check } from "lucide-react";
import type { BatchResponse, EvaluationResponse } from "../../gen/models";
import { EntityLinkedText } from "./EntityLinkedText";
import { StructuredEvaluationView } from "./StructuredEvaluationView";
import { parseReasoningToDetails } from "./parseReasoningToDetails";

interface ScorecardSummaryViewProps {
  activeEval?: EvaluationResponse | null;
  onEnterHumanMode: () => void;
  onArchive?: (evalId: string) => void;
  isArchiving?: boolean;
  activeBatch?: BatchResponse | null;
  allBatches?: BatchResponse[];
  onAssignToActiveBatch?: (evalId: string) => Promise<void>;
  onOpenBatchAssignModal?: (evalItem: EvaluationResponse) => void;
  isAssigning?: boolean;
}

export function ScorecardSummaryView({
  activeEval,
  onEnterHumanMode,
  onArchive,
  isArchiving,
  activeBatch,
  allBatches = [],
  onAssignToActiveBatch,
  onOpenBatchAssignModal,
  isAssigning = false,
}: ScorecardSummaryViewProps) {
  const assignedBatch = allBatches.find((b) => b.id === activeEval?.batch_id);

  const effectiveDetails = useMemo(() => {
    const details = activeEval?.details;
    const hasRules = details?.rule_evaluations && details.rule_evaluations.length > 0;

    if (!hasRules && activeEval?.reasoning) {
      const parsed = parseReasoningToDetails(activeEval.reasoning);
      if (parsed) {
        if (!details) {
          return parsed;
        }
        return {
          ...details,
          summary: details.summary || parsed.summary,
          rule_evaluations:
            details.rule_evaluations && details.rule_evaluations.length > 0
              ? details.rule_evaluations
              : parsed.rule_evaluations,
          knowledge_citations:
            details.knowledge_citations && details.knowledge_citations.length > 0
              ? details.knowledge_citations
              : parsed.knowledge_citations,
          dimension_critiques:
            details.dimension_critiques && Object.keys(details.dimension_critiques).length > 0
              ? details.dimension_critiques
              : parsed.dimension_critiques,
          entity_references:
            details.entity_references && details.entity_references.length > 0
              ? details.entity_references
              : parsed.entity_references,
        };
      }
    }
    return details || null;
  }, [activeEval?.details, activeEval?.reasoning]);
  const getScoreColor = (score?: number | null) => {
    if (score == null) return "text-stone-400 bg-stone-100 dark:bg-stone-800 border-stone-200 dark:border-stone-700";
    if (score >= 4.0) return "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 border-emerald-200 dark:border-emerald-800";
    if (score >= 3.5) return "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800";
    return "text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/60 border-rose-200 dark:border-rose-800";
  };

  const renderMetricBar = (label: string, score?: number | null) => {
    const val = score ?? 0;
    const pct = Math.min(Math.max((val / 5) * 100, 0), 100);
    const barColor =
      val >= 4.0 ? "bg-emerald-500" : val >= 3.5 ? "bg-amber-500" : val > 0 ? "bg-rose-500" : "bg-stone-300 dark:bg-stone-700";

    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-stone-600 dark:text-stone-400 font-medium">{label}</span>
          <span className="font-mono font-semibold text-stone-900 dark:text-stone-100">
            {score != null ? score.toFixed(2) : "N/A"}
          </span>
        </div>
        <div className="h-1.5 w-full bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Overall Score Card */}
      <div className="p-4 rounded-xl bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 flex items-center justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-400">
            Composite Score
          </span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-extrabold text-stone-900 dark:text-stone-100 font-mono">
              {activeEval?.score != null ? Number(activeEval.score).toFixed(2) : "Unrated"}
            </span>
            <span className="text-xs text-stone-400">/ 5.00</span>
          </div>
        </div>

        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border capitalize ${getScoreColor(activeEval?.score)}`}>
          {activeEval?.status || "Pending"}
        </span>
      </div>

      {/* Hallucination Alert Banner */}
      {activeEval?.hallucination_detected && (
        <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800 flex items-start gap-2.5">
          <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-xs font-bold text-rose-900 dark:text-rose-200">Hallucination Flagged</h4>
            <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-0.5 leading-snug">
              The evaluator detected ungrounded claims or fictitious rates/amenities inconsistent with authoritative knowledge items.
            </p>
          </div>
        </div>
      )}

      {/* Sub-Metric Columns */}
      <div className="space-y-3 bg-white dark:bg-stone-900 p-4 rounded-xl border border-stone-200 dark:border-stone-800 shadow-2xs">
        <h3 className="text-xs font-bold text-stone-900 dark:text-stone-100 uppercase tracking-wider">
          Dimensional Breakdown
        </h3>
        <div className="space-y-3 pt-1">
          {renderMetricBar("Accuracy Score", activeEval?.accuracy_score)}
          {renderMetricBar("Instruction Adherence", activeEval?.instruction_adherence_score)}
          {renderMetricBar("Tone & Courtesy", activeEval?.tone_score)}
          {renderMetricBar("Tool Efficiency", activeEval?.tool_efficiency_score)}
        </div>
      </div>

      {/* Evaluator Metadata */}
      <div className="space-y-2 text-xs text-stone-500 dark:text-stone-400 border-t border-stone-100 dark:border-stone-800 pt-3">
        <div className="flex items-center justify-between">
          <span>Evaluator Type:</span>
          <span className="font-semibold text-stone-800 dark:text-stone-200 capitalize flex items-center gap-1">
            {activeEval?.evaluator_type === "human" ? <User className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
            <span>{activeEval?.evaluator_type || "Automated (Judge Agent)"}</span>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span>Evaluator:</span>
          <span className="font-medium text-stone-700 dark:text-stone-300 text-[11px]">
            {activeEval?.user_name || activeEval?.user_id || "evaluator-online-llm"}
          </span>
        </div>
        {activeEval?.created_at && (
          <div className="flex items-center justify-between">
            <span>Evaluated At:</span>
            <span className="text-[11px] flex items-center gap-1">
              <Clock className="w-3 h-3 text-stone-400" />
              <span>{new Date(activeEval.created_at).toLocaleString()}</span>
            </span>
          </div>
        )}
        {activeEval?.batch_id && (
          <div className="flex items-center justify-between">
            <span>Assigned Batch:</span>
            <span className="font-semibold text-emerald-700 dark:text-emerald-300 text-[11px] flex items-center gap-1">
              <Layers className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
              <span>{assignedBatch?.title || "Assigned"}</span>
            </span>
          </div>
        )}
      </div>

      {/* Evaluation Findings & Details */}
      {effectiveDetails &&
      (effectiveDetails.summary ||
        (effectiveDetails.rule_evaluations && effectiveDetails.rule_evaluations.length > 0) ||
        (effectiveDetails.knowledge_citations && effectiveDetails.knowledge_citations.length > 0) ||
        (effectiveDetails.entity_references && effectiveDetails.entity_references.length > 0)) ? (
        <div className="space-y-1.5">
          <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
            Evaluation Assessment
          </h4>
          <StructuredEvaluationView details={effectiveDetails} />
        </div>
      ) : activeEval?.reasoning ? (
        <div className="space-y-1.5">
          <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
            Evaluator Reasoning
          </h4>
          <div className="p-3 rounded-xl bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 text-xs text-stone-700 dark:text-stone-300 leading-relaxed whitespace-pre-wrap">
            <EntityLinkedText text={activeEval.reasoning} />
          </div>
        </div>
      ) : null}

      {/* Batch Assignment Section */}
      {activeEval && (
        <div className="p-3.5 rounded-xl bg-stone-50 dark:bg-stone-950/70 border border-stone-200 dark:border-stone-800 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold text-stone-900 dark:text-stone-100">
              <Layers className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Feedback Batch</span>
            </div>
            {activeEval.batch_id ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
                <Check className="w-3 h-3" />
                <span className="max-w-[150px] truncate">{assignedBatch?.title || "Assigned"}</span>
              </span>
            ) : (
              <span className="text-[11px] text-stone-400">Unassigned</span>
            )}
          </div>

          <div className="flex items-center gap-2 pt-0.5">
            {activeBatch ? (
              <>
                <button
                  type="button"
                  onClick={() => onAssignToActiveBatch?.(activeEval.id)}
                  disabled={isAssigning || activeEval.batch_id === activeBatch.id}
                  className="flex-1 py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer disabled:opacity-50"
                  title={`Add this evaluation to ${activeBatch.title}`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>
                    {isAssigning
                      ? "Assigning..."
                      : activeEval.batch_id === activeBatch.id
                      ? "In Current Batch"
                      : `Add to ${activeBatch.title}`}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onOpenBatchAssignModal?.(activeEval)}
                  className="py-2 px-3 rounded-xl border border-stone-200 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-medium transition cursor-pointer whitespace-nowrap"
                  title="Assign this evaluation to a new or different batch"
                >
                  New Batch...
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => onOpenBatchAssignModal?.(activeEval)}
                className="w-full py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Add to Batch</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="pt-2 space-y-2">
        <button
          type="button"
          onClick={onEnterHumanMode}
          className="w-full py-2.5 rounded-xl bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-bold hover:bg-stone-800 dark:hover:bg-white transition flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
        >
          <Sliders className="w-3.5 h-3.5" />
          <span>Leave Human Review</span>
        </button>

        {activeEval?.evaluator_type === "human" && activeEval.status !== "archived" && onArchive && (
          <button
            type="button"
            onClick={() => onArchive(activeEval.id)}
            disabled={isArchiving}
            className="w-full py-2.5 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/50 text-xs font-semibold transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            <Archive className="w-3.5 h-3.5" />
            <span>{isArchiving ? "Archiving..." : "Archive Human Review"}</span>
          </button>
        )}
      </div>
    </div>
  );
}
